import React from "react";

const useSortableData = (items, config = null) => {
  const [sortConfig, setSortConfig] = React.useState(config);

  const normalized = (value) => {
    if (value === null || value === undefined) return "";
    // if it's a Date object
    if (value instanceof Date) return value.getTime();
    // if looks like a date string (ISO-ish), try to parse
    if (typeof value === "string") {
      const time = Date.parse(value);
      if (!Number.isNaN(time) && value.includes("-")) return time; // crude ISO date detection
    }
    return value;
  };

  const sortedItems = React.useMemo(() => {
    let sortableItems = [...items];

    if (sortConfig !== null) {
      const { key, direction } = sortConfig;

      sortableItems.sort((a, b) => {
        const vaRaw = a?.[key];
        const vbRaw = b?.[key];

        const va = normalized(vaRaw);
        const vb = normalized(vbRaw);

        // If both are strings -> use localeCompare case-insensitive and numeric-aware
        if (typeof va === "string" && typeof vb === "string") {
          const cmp = va.localeCompare(vb, undefined, {
            sensitivity: "base", // ignores case and diacritics
            numeric: true, // "2" < "10" correctly
          });
          return direction === "ascending" ? cmp : -cmp;
        }

        // If both are numbers (or parsed dates as numbers)
        if (typeof va === "number" && typeof vb === "number") {
          if (va < vb) return direction === "ascending" ? -1 : 1;
          if (va > vb) return direction === "ascending" ? 1 : -1;
          return 0;
        }

        // Fallback to default comparisons (stringified)
        const sa = String(va);
        const sb = String(vb);
        const cmp = sa.localeCompare(sb, undefined, {
          sensitivity: "base",
          numeric: true,
        });
        return direction === "ascending" ? cmp : -cmp;
      });
    }
    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  return { items: sortedItems, requestSort, sortConfig };
};

export default useSortableData;
